import React, { Suspense, useEffect, useMemo } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import Loader from 'components/Loader/Loader';
import { replace } from 'connected-react-router';

import { getSpotsAction } from 'actions/actions';
import AddSpot from 'components/Dashboard/AddSpot/AddSpot';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';
import { AppState } from 'reducers/reducers';
import { selectedOrganizationId, selectedSpotId } from 'selectors';
import CalendarModuleContainer from './CalendarModuleContainer';
import RatesCalendarModuleContainer from './RatesCalendarModuleContainer';
import SlotsModuleContainer from './SlotsModuleContainer';
import AggregatorRentalsContainer from './AggregatorRentalsContainer';
import VisitorsModuleContainer from './VisitorsModuleContainer';
import VisitorCodesModuleContainer from './VisitorCodesModuleContainer';
import PromoCodesModuleContainer from './PromoCodesModuleContainer';
import ReportsModuleContainer from './ReportsModuleContainer';
import UploadVisitorsCSVModuleContainer from './UploadVisitorsCSVModuleContainer';
import ValidationGeneratorModuleContainer from './ValidationGeneratorModuleContainer';
import SpotDetailContainer from './SpotDetailContainer';
import SpotTimelineContainer from './LotTimelineContainer';
import TicketModuleContainer from './TicketModuleContainer';
import DriversModuleContainer from './DriversModuleContainer';
import TenantsContainer from './TenantsContainer';
import VisibilityContainer from './Visibility';
import { VisitorsProvider } from 'contexts/VisitorsContext';
import RevenueContainer from './RevenueContainer';
import { LocalIntelligenceContainer } from './LocalIntelligence';
import PropertyInsightsContainer from './PropertyInsightsContainer';
import ChatContainer from './ChatContainer';

type SpotsContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const SpotsContainer = ({
    orgSpots,
    organization,
    orgSpotsLoading,
    spotIdInUrl,
    orgIdInUrl,
    replace: connectedReplace,
    getSpots,
}: SpotsContainerProps): JSX.Element => {
    useEffect(() => {
        getSpots();
    }, [getSpots]);

    const { isExact: isSpotsIndexRoute } = useRouteMatch('/organization/:orgId/spots') || { isExact: false };
    const { isExact: isSpotPageWithoutPath } = useRouteMatch<{ orgId: string; spotId: string }>(
        '/organization/:orgId/spots/:spotId'
    ) || { isExact: false };

    const spotArr = useMemo(() => orgSpots && Object.values(orgSpots), [orgSpots]);
    const hasAtLeastOneSpot = spotArr ? spotArr.length > 0 : null;
    const selectedSpot = useMemo(
        () => (orgSpots && spotIdInUrl ? orgSpots[spotIdInUrl] || null : null),
        [orgSpots, spotIdInUrl]
    );

    let spotIdInUrlIsInThisOrg = true;
    if (orgSpots && spotIdInUrl && !(spotIdInUrl in orgSpots)) {
        spotIdInUrlIsInThisOrg = false;
    }

    useEffect(() => {
        // New organization will load soon, don't do anything
        if (!organization?.pk) return;
        if (organization.pk !== orgIdInUrl) return;

        if (isSpotsIndexRoute && orgSpots && hasAtLeastOneSpot) {
            if (!spotArr?.[0]?.pk) {
                return;
            }
            connectedReplace(`/organization/${organization.pk}/spots/${spotArr[0].pk}/detail`);
        } else {
            // We are in some sort of spot-specific page (with or without a path after it)
            // so spotIdInUrl should be a valid number
            if (!spotIdInUrlIsInThisOrg) {
                if (!spotArr?.[0]?.pk) {
                    return;
                }
                connectedReplace(`/organization/${organization.pk}/spots/${spotArr[0].pk}/detail`);
            } else if (isSpotPageWithoutPath) {
                connectedReplace(`/organization/${organization.pk}/spots/${spotIdInUrl}/detail`);
            }
        }
    }, [
        organization?.pk,
        orgIdInUrl,
        isSpotsIndexRoute,
        spotArr,
        hasAtLeastOneSpot,
        spotIdInUrlIsInThisOrg,
        isSpotPageWithoutPath,
        spotIdInUrl,
        orgSpots,
        connectedReplace,
    ]);

    if (hasAtLeastOneSpot) {
        return (
            <Suspense fallback={<Loader />}>
                <Switch>
                    <Route path="/organization/:organizationId/spots/:spotId/detail">
                        <SpotDetailContainer />
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/events">
                        <DashboardContent title="Events">
                            <CalendarModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/rates">
                        <AuthorizedView allowed={Roles.Superuser}>
                            <DashboardContent title="Rates">
                                <RatesCalendarModuleContainer />
                            </DashboardContent>
                        </AuthorizedView>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/invoice">
                        <AuthorizedView allowed={Roles.Superuser}>
                            <DashboardContent title="Create an Invoice">
                                <SlotsModuleContainer />
                            </DashboardContent>
                        </AuthorizedView>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/aggregator-rentals">
                        <DashboardContent title="Aggregator Rentals">
                            <AggregatorRentalsContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/visitors">
                        <DashboardContent title="Visitors">
                            <VisitorsModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/codes">
                        <DashboardContent title="Validation Codes">
                            <VisitorCodesModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/promos">
                        <DashboardContent title="Discount Codes">
                            <PromoCodesModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/drivers">
                        <DashboardContent title="Drivers">
                            <DriversModuleContainer />
                        </DashboardContent>
                    </Route>
                    {/* Not linked from the sidebar until the Tenants page is ready. */}
                    <Route path="/organization/:organizationId/spots/:spotId/tenants">
                        <TenantsContainer />
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/reports">
                        <DashboardContent title="Reports">
                            <ReportsModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/upload-visitors-csv">
                        <DashboardContent title="Visitor CSV Upload">
                            <VisitorsProvider>
                                <UploadVisitorsCSVModuleContainer />
                            </VisitorsProvider>
                        </DashboardContent>
                    </Route>

                    <Route path="/organization/:organizationId/spots/:spotId/kiosk-generator">
                        <DashboardContent title="Validation Kiosk Setup">
                            <ValidationGeneratorModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/property-insights">
                        <PropertyInsightsContainer />
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/timeline">
                        <DashboardContent title="Timeline">
                            <SpotTimelineContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/violations">
                        <DashboardContent title="Waive a Violation">
                            <TicketModuleContainer />
                        </DashboardContent>
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/visibility">
                        {selectedSpot ? <VisibilityContainer /> : <Loader />}
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/revenue">
                        <RevenueContainer />
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/local-intelligence">
                        <LocalIntelligenceContainer />
                    </Route>
                    <Route path="/organization/:organizationId/spots/:spotId/chat">
                        <ChatContainer />
                    </Route>
                </Switch>
            </Suspense>
        );
    }

    return (
        <div style={{ height: 'calc(100vh - var(--env-banner-height, 0px))', width: '100%' }}>
            {orgSpotsLoading ? (
                <Loader centered />
            ) : (
                // There are no spots for this organization yet.
                <>
                    <div>
                        <h3>No parking lots here yet...</h3>
                    </div>
                    {organization?.pk != null ? <AddSpot organizationId={organization.pk} /> : null}
                </>
            )}
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        orgSpots: state.spots.orgSpots,
        orgSpotsLoading: state.spots.orgSpotsLoading,
        spotIdInUrl: selectedSpotId(state),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organization: state.organization.organization!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        orgIdInUrl: selectedOrganizationId(state)!,
    };
}

const mapDispatchToProps = {
    getSpots: getSpotsAction,
    replace,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpotsContainer);
